import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { FacilitiesService, FACILITY_TYPES, EQUIPMENT_TYPES, MAINTENANCE_STATUS } from '../../services/facilitiesService';

const FacilityManagement = () => {
  const [activeTab, setActiveTab] = useState('facilities');
  const [facilities, setFacilities] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BuildingOfficeIcon },
    { id: 'facilities', name: 'Facilities', icon: BuildingOfficeIcon },
    { id: 'maintenance', name: 'Maintenance', icon: WrenchScrewdriverIcon },
    { id: 'equipment', name: 'Equipment', icon: CubeIcon }
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Handle image preview when modal opens with existing item
  useEffect(() => {
    if (showModal && selectedItem && selectedItem.images && selectedItem.images.length > 0) {
      const imageUrl = selectedItem.images[0];
      if (imageUrl.startsWith('/images/')) {
        setPreviewUrl(`https://via.placeholder.com/400x300/e5e7eb/6b7280?text=Facility+Image`);
      } else {
        setPreviewUrl(imageUrl);
      }
    } else if (showModal && !selectedItem) {
      setPreviewUrl('');
    }
  }, [showModal, selectedItem]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [facilitiesData, equipmentData, maintenanceData] = await Promise.all([
        FacilitiesService.getFacilities(),
        FacilitiesService.getEquipment(),
        FacilitiesService.getMaintenanceRequests()
      ]);
      

      
      setFacilities(facilitiesData);
      setEquipment(equipmentData);
      setMaintenanceRequests(maintenanceData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, item = null) => {

    setModalType(type);
    setSelectedItem(item);
    
    // Process form data based on type and item
    let processedFormData = item ? { ...item } : {};
    
    // For facility editing, parse JSON strings to arrays
    if (type === 'facility' && item) {
      if (typeof item.features === 'string') {
        try {
          processedFormData.features = JSON.parse(item.features);
        } catch (e) {
          processedFormData.features = [];
        }
      }
      if (typeof item.amenities === 'string') {
        try {
          processedFormData.amenities = JSON.parse(item.amenities);
        } catch (e) {
          processedFormData.amenities = [];
        }
      }
      if (typeof item.contact === 'string') {
        try {
          processedFormData.contact = JSON.parse(item.contact);
        } catch (e) {
          processedFormData.contact = {};
        }
      }
    }
    
    setFormData(processedFormData);
    setSelectedFile(null);

    setShowModal(true);
  };

  const closeModal = () => {

    setShowModal(false);
    setModalType('');
    setSelectedItem(null);
    setFormData({});
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (formData) => {

    try {
      switch (modalType) {
        case 'facility':
          let facilityData = formData;
          
          // If there's a selected file, create FormData for file upload
          if (selectedFile) {
            const submitData = new FormData();
            Object.keys(formData).forEach(key => {
              const value = formData[key];
              if (Array.isArray(value)) {
                // Convert arrays to JSON strings for FormData
                submitData.append(key, JSON.stringify(value));
              } else if (typeof value === 'object' && value !== null) {
                // Convert objects to JSON strings for FormData
                submitData.append(key, JSON.stringify(value));
              } else {
                submitData.append(key, value || '');
              }
            });
            submitData.append('image', selectedFile);
            facilityData = submitData;
          }
          
  
          if (selectedItem) {
            await FacilitiesService.updateFacility(selectedItem.id, facilityData);
          } else {
            await FacilitiesService.createFacility(facilityData);
          }
          break;

        case 'equipment':
          if (selectedItem) {
            await FacilitiesService.updateEquipment(selectedItem.id, formData);
          } else {
            await FacilitiesService.createEquipment(formData);
          }
          break;
        case 'maintenance':
          if (selectedItem) {
            await FacilitiesService.updateMaintenanceRequest(selectedItem.id, formData);
          } else {
            await FacilitiesService.createMaintenanceRequest(formData);
          }
          break;
      }

      closeModal();
      loadData();
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      switch (type) {
        case 'facility':
          await FacilitiesService.deleteFacility(id);
          break;

        case 'equipment':
          await FacilitiesService.deleteEquipment(id);
          break;
        case 'maintenance':
          await FacilitiesService.deleteMaintenanceRequest(id);
          break;
      }
      loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const getStatusColor = (status, type) => {
    const colors = {
      [MAINTENANCE_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800',
      [MAINTENANCE_STATUS.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
      [MAINTENANCE_STATUS.COMPLETED]: 'bg-green-100 text-green-800',
      [MAINTENANCE_STATUS.CANCELLED]: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const renderOverview = () => {
    const stats = {
      totalFacilities: facilities.length,
      pendingMaintenance: maintenanceRequests.filter(m => m.status === MAINTENANCE_STATUS.PENDING).length,
      totalEquipment: equipment.length
    };

    const urgentMaintenance = maintenanceRequests.filter(m => m.priority === 'high' && m.status !== MAINTENANCE_STATUS.COMPLETED).slice(0, 3);

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Facilities</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalFacilities}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <WrenchScrewdriverIcon className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Maintenance</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingMaintenance}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <CubeIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Equipment Items</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEquipment}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="max-w-2xl">
          {/* Urgent Maintenance */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Urgent Maintenance</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {urgentMaintenance.map((request) => (
                  <div key={request.id} className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-3" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{request.facilityName}</p>
                      <p className="text-sm text-gray-600">{request.description}</p>
                      <p className="text-xs text-gray-500 mt-1">Reported: {request.reportedDate}</p>
                    </div>
                  </div>
                ))}
                {urgentMaintenance.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No urgent maintenance requests</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFacilities = () => {

    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Facilities Management</h2>
          <button
            onClick={() => {
          
              openModal('facility');
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Facility
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {facilities.map((facility) => (
          <div key={facility.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="h-48 bg-gray-200 relative">
              {facility.images && facility.images.length > 0 ? (
                <img 
                  src={facility.images[0]} 
                  alt={facility.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/400x300/e5e7eb/6b7280?text=Facility+Image`;
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <PhotoIcon className="h-16 w-16 text-gray-400" />
                  <span className="ml-2 text-gray-500 text-sm">No Image</span>
                </div>
              )}
            </div>
            
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{facility.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{facility.description}</p>
              
              <div className="flex justify-between items-center">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  {facility.type}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                  
                      openModal('facility', facility);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete('facility', facility.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    );
  };



  const renderMaintenance = () => {
    const filteredRequests = maintenanceRequests.filter(request => {
      const matchesSearch = request.facilityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           request.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' || request.status === filterStatus;
      return matchesSearch && matchesFilter;
    });

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Maintenance Requests</h2>
          <button
            onClick={() => openModal('maintenance')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Request
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search maintenance requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md"
          >
            <option value="all">All Status</option>
            <option value={MAINTENANCE_STATUS.PENDING}>Pending</option>
            <option value={MAINTENANCE_STATUS.IN_PROGRESS}>In Progress</option>
            <option value={MAINTENANCE_STATUS.COMPLETED}>Completed</option>
          </select>
        </div>

        {/* Maintenance Requests */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredRequests.map((request) => (
              <li key={request.id}>
                <div className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <WrenchScrewdriverIcon className="h-10 w-10 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">{request.facilityName}</p>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            {request.status}
                          </span>
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                            request.priority === 'high' ? 'bg-red-100 text-red-800' :
                            request.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {request.priority} priority
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">{request.description}</p>
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          <p>Reported by {request.reportedBy}</p>
                          <ClockIcon className="flex-shrink-0 ml-4 mr-1.5 h-4 w-4" />
                          <p>{request.reportedDate}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openModal('maintenance', request)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete('maintenance', request.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderEquipment = () => {
    const filteredEquipment = equipment.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.category.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Equipment Inventory</h2>
          <button
            onClick={() => openModal('equipment')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Equipment
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search equipment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full"
          />
        </div>

        {/* Equipment Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEquipment.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <CubeIcon className="h-8 w-8 text-blue-600" />
                <div className="flex space-x-2">
                  <button
                    onClick={() => openModal('equipment', item)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete('equipment', item.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{item.description}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">{item.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quantity:</span>
                  <span className="font-medium">{item.quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Available:</span>
                  <span className={`font-medium ${
                    item.available > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {item.available}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Condition:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    item.condition === 'excellent' ? 'bg-green-100 text-green-800' :
                    item.condition === 'good' ? 'bg-blue-100 text-blue-800' :
                    item.condition === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {item.condition}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'facilities':
        return renderFacilities();
      case 'maintenance':
        return renderMaintenance();
      case 'equipment':
        return renderEquipment();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Facility Management</h1>
        <p className="mt-2 text-gray-600">
          Manage facilities, maintenance, and equipment inventory.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Facility Modal - Rebuilt from scratch */}
      {showModal && modalType === 'facility' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedItem ? 'Edit Facility' : 'Add New Facility'}
              </h3>
              <button 
                onClick={() => {
          
                  closeModal();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Simple form fields */}
            <div className="space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Image
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {previewUrl ? (
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewUrl('');
                          setSelectedFile(null);
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-2">
                        <label className="cursor-pointer">
                          <span className="text-sm text-gray-600">
                            Click to upload or drag and drop
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facility Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => {
                    setFormData({...formData, name: e.target.value});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter facility name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facility Type *
                </label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => {
                    setFormData({...formData, type: e.target.value});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  {Object.values(FACILITY_TYPES).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => {
                    setFormData({...formData, description: e.target.value});
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the facility"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacity
                </label>
                <input
                  type="number"
                  value={formData.capacity || ''}
                  onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max capacity"
                />
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  closeModal();
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  
                  // Validate required fields
                   if (!formData.name || !formData.type) {
                     alert('Please fill in required fields (Name and Type)');
                     return;
                   }
                  
                  try {

                    
                    let facilityData = formData;
                    
                    // If there's a selected file, create FormData for file upload
                    if (selectedFile) {

                      const submitData = new FormData();
                      Object.keys(formData).forEach(key => {
                        const value = formData[key];
                        if (Array.isArray(value)) {
                          // Convert arrays to JSON strings for FormData
                          submitData.append(key, JSON.stringify(value));
                        } else if (typeof value === 'object' && value !== null) {
                          // Convert objects to JSON strings for FormData
                          submitData.append(key, JSON.stringify(value));
                        } else {
                          submitData.append(key, value || '');
                        }
                      });
                      submitData.append('image', selectedFile);
                      facilityData = submitData;
                    }
                    
                    if (selectedItem) {
                      // Update existing facility
                      await FacilitiesService.updateFacility(selectedItem.id, facilityData);

                    } else {
                      // Create new facility
                      await FacilitiesService.createFacility(facilityData);
                    }
                    
                    // Close modal and refresh data
                    closeModal();
                    loadData();
                    
                  } catch (error) {
                    console.error('Error creating/updating facility:', error);
                    alert('Error: ' + (error.message || 'Failed to save facility'));
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {selectedItem ? 'Update' : 'Create'} Facility
              </button>
            </div>
          </div>
        </div>
      )}
      

      
      {/* Equipment Modal */}
      {showModal && modalType === 'equipment' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedItem ? 'Edit Equipment' : 'Add New Equipment'}
              </h3>
              <button onClick={closeModal}>
                <XMarkIcon className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(formData); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Category</option>
                  {Object.values(EQUIPMENT_TYPES).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity || ''}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condition
                  </label>
                  <select
                    value={formData.condition || 'good'}
                    onChange={(e) => setFormData({...formData, condition: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Storage location"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase Date
                </label>
                <input
                  type="date"
                  value={formData.purchaseDate || ''}
                  onChange={(e) => setFormData({...formData, purchaseDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional notes about the equipment"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {selectedItem ? 'Update' : 'Add'} Equipment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Maintenance Modal */}
      {showModal && modalType === 'maintenance' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedItem ? 'Edit Maintenance Request' : 'New Maintenance Request'}
              </h3>
              <button onClick={closeModal}>
                <XMarkIcon className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(formData); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facility *
                </label>
                <select
                  value={formData.facilityId || ''}
                  onChange={(e) => setFormData({...formData, facilityId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Facility</option>
                  {facilities.map(facility => (
                    <option key={facility.id} value={facility.id}>{facility.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issue Type *
                </label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Issue Type</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="HVAC">HVAC</option>
                  <option value="Structural">Structural</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Cleaning">Cleaning</option>
                  <option value="Safety">Safety</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority *
                </label>
                <select
                  value={formData.priority || 'medium'}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the maintenance issue in detail"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reported By *
                </label>
                <input
                  type="text"
                  value={formData.reportedBy || ''}
                  onChange={(e) => setFormData({...formData, reportedBy: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Name of person reporting the issue"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contactEmail || ''}
                  onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status || 'pending'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.values(MAINTENANCE_STATUS).map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {selectedItem ? 'Update' : 'Submit'} Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityManagement;